"use client";

import TestimonialCard from "@/components/home/TestimonialCard";
import { testimonials } from "@/data/testimonials";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import IconBox from "../common/IconBox";

export default function TestimonialsSection() {
  const containerRef = useRef<HTMLElement>(null);
  const x = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Header opacity - fades out BEFORE cards appear
  const headerOpacity = useTransform(smoothProgress, [0, 0.15], [1, 0]);

  // Cards initial position - starts from bottom, moves up AFTER title fades
  const cardsY = useTransform(smoothProgress, [0.15, 0.3], [300, 0]);
  const cardsOpacity = useTransform(smoothProgress, [0.15, 0.25], [0, 1]);

  // Horizontal scroll for cards - starts after cards are in position
  const scrollOffset = useTransform(
    smoothProgress,
    [0.35, 1],
    [0, -(testimonials.length - 3) * 360]
  );

  // Update x position based on scroll when not dragging
  useEffect(() => {
    if (!isDragging) {
      const unsubscribe = scrollOffset.on("change", (latest) => {
        x.set(latest);
      });
      return () => unsubscribe();
    }
  }, [isDragging, scrollOffset, x]);

  return (
    <>
      {/* Mobile Version */}
      <div className="flex flex-col items-center overflow-hidden py-20 md:hidden">
        <div className="mb-10 flex flex-col items-center">
          <IconBox icon={IoChatboxEllipsesOutline} />
          <h2 className="font-raleway text-2xl font-bold text-white">
            Testimonials
          </h2>
        </div>
        <div className="flex w-full cursor-grab overflow-hidden active:cursor-grabbing">
          <motion.div
            drag="x"
            dragConstraints={{
              left: -(testimonials.length * 286),
              right: 0,
            }}
            dragElastic={0.1}
            dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
            initial={{ x: 0 }}
            animate={{ x: "-50%" }}
            transition={{
              duration: 40,
              ease: "linear",
              repeat: Infinity,
            }}
            className="flex min-w-max gap-6 px-4"
          >
            {[...testimonials, ...testimonials].map((item, index) => (
              <div key={index} className="w-[280px] shrink-0">
                <TestimonialCard data={item} />
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Desktop Version - Scroll Animation */}
      <section
        ref={containerRef}
        className="bg-brand-black relative hidden md:block"
        style={{ height: `${700}vh` }}
      >
        <div className="sticky top-0 flex h-screen w-full items-center justify-center overflow-hidden">
          {/* Header - Fades out BEFORE cards appear */}
          <motion.div
            style={{ opacity: headerOpacity }}
            className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center"
          >
            <IconBox icon={IoChatboxEllipsesOutline} />
            <h2 className="font-raleway text-[2rem] font-bold text-white">
              Testimonials
            </h2>
          </motion.div>

          {/* Cards Container */}
          <motion.div
            style={{
              y: cardsY,
              opacity: cardsOpacity,
            }}
            className="relative flex w-full cursor-grab items-center justify-start overflow-hidden pl-[calc(50vw-510px)] active:cursor-grabbing"
          >
            <motion.div
              drag="x"
              dragConstraints={{
                left: -(testimonials.length - 3) * 360,
                right: 0,
              }}
              dragElastic={0.1}
              dragTransition={{ bounceStiffness: 600, bounceDamping: 20 }}
              onDragStart={() => setIsDragging(true)}
              onDragEnd={() => setIsDragging(false)}
              style={{ x }}
              className="flex gap-5"
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="w-[340px] shrink-0">
                  <TestimonialCard data={testimonial} />
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
